/**
 * Tuần 1 phiên bản mới:
 *   - `CacLanDen`: thẻ khách → các lần đến (F05, T8).
 *   - `TheBaoCao`: Sổ tiền → Báo cáo doanh thu theo ngày / thợ / dịch vụ trong một tháng (F09, T9).
 * Mọi con số do MÁY CHỦ tính (tiền là chuỗi số nguyên đồng, ADR-026); màn này chỉ hiện.
 */
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { useEffect, useState } from 'react'
import { type BaoCaoDoanhThu, baoCao, khachApi, type LanDen, ngayVn, tienVnd } from './api.ts'
import { loiCua, lucViet, ngayViet } from './GoiVi.tsx'
import { cacDongCuaTho, hauToDaNgung, tomTatChiNhanh } from './so-tien-chi-nhanh.ts'
import { S } from './text/so-tien.ts'
import { ThongBao, Xuong } from './ui.tsx'

export function CacLanDen({ customerId }: { customerId: string }) {
  const [ds, setDs] = useState<LanDen[] | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    void khachApi
      .lichSu(customerId)
      .then((r) => setDs(r.lanDen))
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [customerId])
  return (
    <div className="muc-to">
      <h3>{S.lanDenTitle}</h3>
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {ds === null ? (
        loi === null && <Xuong cao={60} />
      ) : ds.length === 0 ? (
        <p className="chu-phu">{S.lanDenTrong}</p>
      ) : (
        <ul className="ds-so">
          {ds.map((x) => (
            <li key={x.appointmentId}>
              <span>
                <b>{lucViet(x.batDau).slice(0, 10)}</b> · {x.dichVu.join(', ')}
                {/* Tên thợ là dòng phụ riêng: dòng nào cũng hai tầng, không bẻ giữa tên dịch vụ (ui-critic v25). */}
                {x.tho.length > 0 && <span className="chu-phu dong-tho">{x.tho.join(', ')}</span>}
              </span>
              {/* Cột phải cố định (ui-critic v22): giá đã thu, hoặc trạng thái, hoặc "Gói" (buổi trả bằng gói). */}
              <span className="cot-phai">
                {x.daThuVnd !== null ? (
                  <b>{tienVnd(x.daThuVnd)}</b>
                ) : x.trangThai !== 'done' ? (
                  <span className={`vien-tt tt-${x.trangThai}`}>
                    {S.trangThai[x.trangThai] ?? x.trangThai}
                  </span>
                ) : (
                  <span className="chu-phu">{S.lanDenGoi}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** "2026-10" → ngày đầu, ngày cuối của tháng (lịch Gregory, không múi giờ). */
function khoangThang(thang: string): { tu: string; den: string } {
  const nam = Number.parseInt(thang.slice(0, 4), 10)
  const t = Number.parseInt(thang.slice(5, 7), 10)
  const cuoi = new Date(Date.UTC(nam, t, 0)).getUTCDate()
  return { tu: `${thang}-01`, den: `${thang}-${String(cuoi).padStart(2, '0')}` }
}

function doiThang(thang: string, n: number): string {
  const nam = Number.parseInt(thang.slice(0, 4), 10)
  const t = Number.parseInt(thang.slice(5, 7), 10) - 1 + n
  const nam2 = nam + Math.floor(t / 12)
  const t2 = ((t % 12) + 12) % 12
  return `${nam2}-${String(t2 + 1).padStart(2, '0')}`
}

const CACH: { ma: BaoCaoDoanhThu['theo']; ten: string }[] = [
  { ma: 'ngay', ten: S.bcTheoNgay },
  { ma: 'tho', ten: S.bcTheoTho },
  { ma: 'dich-vu', ten: S.bcTheoDichVu },
]

export function TheBaoCao() {
  const [thang, setThang] = useState(ngayVn().slice(0, 7))
  const [theo, setTheo] = useState<BaoCaoDoanhThu['theo']>('ngay')
  const [bc, setBc] = useState<BaoCaoDoanhThu | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [dangTai, setDangTai] = useState(false)
  const [daTai, setDaTai] = useState(false)
  async function taiTep() {
    setDangTai(true)
    setLoi(null)
    try {
      await baoCao.taiTepThang(thang)
      setDaTai(true)
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDangTai(false)
    }
  }
  useEffect(() => {
    setBc(null)
    setLoi(null)
    setDaTai(false)
    const k = khoangThang(thang)
    void baoCao
      .doanhThu(k.tu, k.den, theo)
      .then(setBc)
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [thang, theo])

  return (
    <section className="the the-dem the-khoi">
      {/* Cùng bộ chọn tháng tiếng Việt với bảng lương (không `<input type="month">`, ui-critic v11). */}
      <div className="dieu-huong-ngay">
        <button
          type="button"
          className="nut nut-icon"
          aria-label={S.bcThangTruoc}
          onClick={() => setThang(doiThang(thang, -1))}
        >
          <ChevronLeft size={22} />
        </button>
        <span className="nhan-ngay">{S.bcThang(thang)}</span>
        <button
          type="button"
          className="nut nut-icon"
          aria-label={S.bcThangSau}
          disabled={thang >= ngayVn().slice(0, 7)}
          onClick={() => setThang(doiThang(thang, 1))}
        >
          <ChevronRight size={22} />
        </button>
      </div>
      <div className="hang-chip chia-deu cot-3" role="tablist" aria-label={S.bcTitle}>
        {CACH.map((c) => (
          <button
            key={c.ma}
            type="button"
            role="tab"
            aria-selected={theo === c.ma}
            className={`chip ${theo === c.ma ? 'dang-chon' : ''}`}
            onClick={() => setTheo(c.ma)}
          >
            {c.ten}
          </button>
        ))}
      </div>
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {bc === null ? (
        loi === null && <Xuong cao={160} />
      ) : (
        <KetQuaBaoCao
          bc={bc}
          theo={theo}
          thang={thang}
          dangTai={dangTai}
          daTai={daTai}
          onTai={() => void taiTep()}
        />
      )}
    </section>
  )
}

/**
 * Phần kết quả của Báo cáo (tách khỏi `TheBaoCao` để vẽ tĩnh trong test). Số luôn là của CẢ TIỆM — không theo chi nhánh đang
 * chọn. Chỉ khi máy chủ báo `nhieuChiNhanh` mới thêm khối "Cả tiệm, gộp n chi nhánh:" + từng chi nhánh, và (cách xem Thợ) dòng
 * phụ chi nhánh của từng thợ. Thiếu trường (máy chủ cũ) hoặc `false` → markup y hệt bản cũ.
 */
export function KetQuaBaoCao({
  bc,
  theo,
  thang,
  dangTai,
  daTai,
  onTai,
}: {
  bc: BaoCaoDoanhThu
  theo: BaoCaoDoanhThu['theo']
  thang: string
  dangTai: boolean
  daTai: boolean
  onTai: () => void
}) {
  const nhieu = bc.nhieuChiNhanh === true
  const dsCn = bc.chiNhanh ?? []
  const tenDong = (d: BaoCaoDoanhThu['dong'][number]): string => {
    if (theo === 'ngay') return ngayViet(d.khoa)
    if (d.khoa === 'san-pham') return S.bcBanSanPham
    if (d.khoa === '') return theo === 'tho' ? S.bcKhongGhiTho : S.bcKhac
    return d.ten
  }
  return (
    <>
      <div className="so-to">
        <span className="nhan-muc">{S.bcTong}</span>
        <b>{tienVnd(bc.tongVnd)}</b>
        <span className="chu-phu">{S.bcSoHoaDon(bc.soHoaDon)}</span>
        {bc.tongGoiVnd !== '0' && (
          <span className="chu-phu">{S.bcGoi(tienVnd(bc.tongGoiVnd))}</span>
        )}
      </div>
      {nhieu && dsCn.length > 0 && theo === 'ngay' && (
        <>
          <span className="chu-phu">{S.bcCaTiem(dsCn.length)}</span>
          {/* Mỗi chi nhánh ≤ 2 dòng: tên + tiền cùng dòng, dòng phụ NGẮN "12 hoá đơn · gói 299.000đ" bên dưới (soát giao diện N1). */}
          <ul className="ds-so bc-chi-nhanh">
            {dsCn.map((c) => (
              <li key={c.khoa}>
                <span>
                  {c.ten}
                  {c.daNgung ? hauToDaNgung : ''}
                </span>
                <b>{tienVnd(c.tienVnd)}</b>
                <span className="chu-phu cn-phu">
                  {S.bcSoHoaDon(c.soHoaDon)}
                  {c.goiVnd !== '0' ? ` · ${S.bcGoiPhu(tienVnd(c.goiVnd))}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {/* Chip Thợ / Dịch vụ: chỉ MỘT đoạn gọn để danh sách thợ / dịch vụ không bị đẩy xuống (phương án lùi của kế hoạch). */}
      {nhieu && dsCn.length > 0 && theo !== 'ngay' && (
        <p className="chu-phu bc-cn-gon">
          {`${S.bcCaTiem(dsCn.length)} ${tomTatChiNhanh(dsCn, tienVnd)}`}
        </p>
      )}
      {bc.soHoaDon > 0 && (
        <>
          <button
            type="button"
            className="nut nut-vien"
            style={{ width: '100%', marginTop: 12 }}
            disabled={dangTai}
            onClick={onTai}
          >
            <Download size={18} />
            {S.bcTai(`${thang.slice(5)}/${thang.slice(0, 4)}`)}
          </button>
          <p className="chu-phu">{daTai ? S.bcDaTai : S.bcTaiPhu}</p>
        </>
      )}
      {bc.dong.length === 0 ? (
        <p className="chu-phu">{S.bcTrong}</p>
      ) : (
        <ul className="ds-so">
          {bc.dong.map((d) => {
            const phu = nhieu && theo === 'tho' ? cacDongCuaTho(d.chiNhanh, tienVnd) : []
            return (
              <li key={d.khoa || 'khac'} className={phu.length > 0 ? 'co-cn' : undefined}>
                <span>
                  {tenDong(d)}
                  {d.goiVnd !== '0' && (
                    <span className="chu-phu"> · {S.bcGoi(tienVnd(d.goiVnd))}</span>
                  )}
                </span>
                <b>{tienVnd(d.tienVnd)}</b>
                {/* MỖI chi nhánh một dòng (tên trái, số phải), nằm trong cùng <li> của thợ, xuống cả bề ngang (soát giao diện N2). */}
                {phu.length > 0 && (
                  <span className="chu-phu dong-cn">
                    {phu.map((p) => (
                      <span key={p.khoa} className="cn-dong">
                        <span>{p.ten}</span>
                        {(p.tien !== '' || p.goi !== '') && (
                          <span className="cn-so">
                            {p.tien !== '' && <span>{p.tien}</span>}
                            {p.goi !== '' && <span>{p.goi}</span>}
                          </span>
                        )}
                      </span>
                    ))}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
